import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle2, Copy, Eye, EyeOff, Loader2, AlertCircle, Zap, Target, ImageIcon, Shield, X } from 'lucide-react';
import { toast } from 'sonner';
import { axiosInstance } from '@/App';

const StripeSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [loading, setLoading] = useState(true);
  const [credentials, setCredentials] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [showUpsell, setShowUpsell] = useState(true);
  const [purchasingElite, setPurchasingElite] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      toast.error('Keine Session ID gefunden');
      navigate('/');
      return;
    }

    // If user is already logged in (buying add-on), redirect to dashboard after brief success message
    const token = localStorage.getItem('token');
    if (token) {
      toast.success('Zahlung erfolgreich! Feature wird freigeschaltet...');
      setTimeout(() => navigate('/dashboard'), 3000);
      return;
    }

    fetchCredentials();
  }, [sessionId]);

  const fetchCredentials = async () => {
    try {
      const response = await axiosInstance.get(`/get-credentials/${sessionId}`);
      
      if (response.data.ready) {
        setCredentials(response.data);
        setLoading(false);
        // Check if user already has Meta Autopilot
        if (response.data.has_meta_autopilot) {
          setShowUpsell(false);
        }
      } else {
        if (retryCount < 15) {
          setTimeout(() => {
            setRetryCount(retryCount + 1);
            fetchCredentials();
          }, response.data.retry_in * 1000 || 2000);
        } else {
          setLoading(false);
          toast.error('Timeout - bitte kontaktiere den Support');
        }
      }
    } catch (error) {
      console.error('Error fetching credentials:', error);
      setLoading(false);
      toast.error('Fehler beim Abrufen der Zugangsdaten');
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} kopiert!`);
  };

  const handleLogin = () => {
    navigate('/');
  };

  const handlePurchaseElite = () => {
    setPurchasingElite(true);
    window.location.href = 'https://buy.stripe.com/bJe00k2JN73PdEJ1mo3F607';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl">
          <CardContent className="pt-12 pb-12 text-center">
            <Loader2 className="w-16 h-16 text-green-500 animate-spin mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Dein Account wird erstellt...
            </h2>
            <p className="text-gray-600">
              Bitte einen Moment Geduld, wir bereiten alles für dich vor.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!credentials) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl border-red-500 border-2">
          <CardContent className="pt-12 pb-12 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Fehler beim Erstellen des Accounts
            </h2>
            <p className="text-gray-600 mb-6">
              Bitte kontaktiere unseren Support: support@we-love-leads.com
            </p>
            <Button onClick={() => navigate('/')}>
              Zur Startseite
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* SUCCESS CONFIRMATION BAR */}
      <div className="bg-green-500 text-white py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <CheckCircle2 className="w-6 h-6 flex-shrink-0" />
          <p className="text-sm md:text-base">
            <span className="font-bold">Zahlung erfolgreich!</span> Deine Zugangsdaten für das Starter-Paket werden gerade generiert und sind in 2-3 Minuten in deinem Postfach.
          </p>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        
        {/* CREDENTIALS SECTION - Compact */}
        <Card className="mb-8 border-2 border-green-200 bg-green-50">
          <CardContent className="pt-6 pb-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h3 className="font-bold text-gray-900 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  {credentials.existing_user ? 'Dein Account ist bereit!' : 'Deine neuen Zugangsdaten:'}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {credentials.existing_user 
                    ? 'Verwende dein bestehendes Passwort zum Einloggen.' 
                    : 'Speichere diese Daten sicher ab!'}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 text-sm">
                <div className="flex items-center gap-2 bg-white px-3 py-2 rounded border">
                  <span className="text-gray-500">User:</span>
                  <code className="font-mono font-bold">{credentials.username}</code>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => copyToClipboard(credentials.username, 'Username')}>
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
                {!credentials.existing_user && credentials.password && (
                  <div className="flex items-center gap-2 bg-white px-3 py-2 rounded border">
                    <span className="text-gray-500">Pass:</span>
                    <code className="font-mono font-bold">{showPassword ? credentials.password : '••••••••'}</code>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                    </Button>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => copyToClipboard(credentials.password, 'Passwort')}>
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ELITE UPSELL SECTION */}
        {showUpsell && (
          <>
            {/* STOPP HEADLINE */}
            <div className="text-center mb-8">
              <h1 className="text-3xl md:text-4xl font-black text-red-600 mb-4">
                STOPP! Hand aufs Herz:
              </h1>
              <p className="text-xl md:text-2xl text-gray-800 font-semibold">
                Willst du jetzt wirklich stundenlang Technik-Puzzles im Meta Werbeanzeigenmanager lösen?
              </p>
            </div>

            {/* THE PROBLEM - Dark Section */}
            <div className="bg-[#0B0E14] text-white rounded-2xl p-8 mb-8">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="text-2xl font-bold text-cyan-400 mb-4">
                    Die Technik-Falle
                  </h2>
                  <p className="text-gray-300 leading-relaxed">
                    Du hast jetzt die perfekte Munition (Creatives & Targeting). Aber die meisten Makler scheitern genau hier: 
                    <span className="text-white font-semibold"> Am komplizierten technischen Setup bei Meta.</span>
                  </p>
                  <p className="text-gray-300 mt-4 leading-relaxed">
                    Werbeagenturen lassen sich genau diesen Part vergoldet bezahlen. Sie verlangen 
                    <span className="text-red-400 font-bold"> 1.500 € monatlich</span>, nur um deine Ads zu verwalten.
                  </p>
                  <p className="text-cyan-400 font-bold text-xl mt-4">
                    Wir beenden dieses Spiel heute.
                  </p>
                </div>
                <div className="hidden md:block">
                  <div className="bg-gradient-to-br from-red-900/30 to-transparent rounded-xl p-6 border border-red-800/50">
                    <div className="text-6xl mb-4">😰</div>
                    <p className="text-gray-400 text-sm">Der typische Makler: Verzweifelt vor hunderten Meta-Einstellungen...</p>
                  </div>
                </div>
              </div>
            </div>

            {/* AGENCY KILLER TABLE */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-center mb-6 text-gray-900">
                Der radikale Vergleich: Agency vs. ELITE
              </h2>
              <div className="overflow-hidden rounded-xl border-2 border-gray-200">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-left p-4 font-semibold text-gray-700">Leistung</th>
                      <th className="text-center p-4 font-semibold text-red-600">Klassische Agentur</th>
                      <th className="text-center p-4 font-semibold text-green-600">FinAds ELITE</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    <tr>
                      <td className="p-4 font-medium">Monatliche Gebühr</td>
                      <td className="p-4 text-center text-red-600 line-through font-bold">1.500 €</td>
                      <td className="p-4 text-center text-green-600 font-bold">0 €</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="p-4 font-medium">Technisches Setup</td>
                      <td className="p-4 text-center text-gray-600">Manuell & Fehleranfällig</td>
                      <td className="p-4 text-center text-green-600 font-semibold">Vollautomatisch (KI)</td>
                    </tr>
                    <tr>
                      <td className="p-4 font-medium">Wartezeit pro Ad</td>
                      <td className="p-4 text-center text-gray-600">Tage bis Wochen</td>
                      <td className="p-4 text-center text-green-600 font-semibold">60 Sekunden</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="p-4 font-medium">Abhängigkeit</td>
                      <td className="p-4 text-center text-gray-600">Knebelverträge</td>
                      <td className="p-4 text-center text-green-600 font-semibold">100% Unabhängig</td>
                    </tr>
                    <tr className="bg-red-50">
                      <td className="p-4 font-bold">Kosten nach 1 Jahr</td>
                      <td className="p-4 text-center text-red-600 line-through font-bold text-xl">18.000 €</td>
                      <td className="p-4 text-center text-green-600 font-bold text-xl">999 €</td>
                    </tr>
                    <tr className="bg-red-100">
                      <td className="p-4 font-bold">Kosten nach 5 Jahren</td>
                      <td className="p-4 text-center text-red-600 line-through font-bold text-xl">90.000 €</td>
                      <td className="p-4 text-center text-green-600 font-bold text-xl">999 €</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* ELITE FEATURES */}
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-8 mb-8 border-2 border-indigo-200">
              <h2 className="text-2xl font-bold text-center mb-8 text-gray-900">
                Das ELITE-Paket: Deine 1-Klick-Freiheit
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex gap-4 bg-white p-5 rounded-xl shadow-sm">
                  <div className="text-3xl">⚡</div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">1-KLICK META SYNC</h3>
                    <p className="text-gray-600 text-sm">Wähle dein Bild, deinen Text und klicke auf "Kampagne veröffentlichen". Unsere Schnittstelle baut die komplette Struktur fehlerfrei direkt in deinem Meta-Konto auf.</p>
                  </div>
                </div>
                <div className="flex gap-4 bg-white p-5 rounded-xl shadow-sm">
                  <div className="text-3xl">🤖</div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">VOLLAUTOMATISCHES TARGETING</h3>
                    <p className="text-gray-600 text-sm">Wir übertragen nicht nur Interessen, sondern bauen die komplette Kampagnen-Struktur (inkl. Retargeting-Ebenen) für dich.</p>
                  </div>
                </div>
                <div className="flex gap-4 bg-white p-5 rounded-xl shadow-sm">
                  <div className="text-3xl">📈</div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">UNLIMITIERTE CREATIVE-POWER</h3>
                    <p className="text-gray-600 text-sm">Erstelle so viele Bilder und Texte, wie du willst. Keine Limits, keine Zusatzkosten. Dein Marketing-Mitarbeiter arbeitet rund um die Uhr.</p>
                  </div>
                </div>
                <div className="flex gap-4 bg-white p-5 rounded-xl shadow-sm">
                  <div className="text-3xl">💎</div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">LIFETIME-GARANTIE</h3>
                    <p className="text-gray-600 text-sm">Einmal zahlen, für immer besitzen. Keine Abos, keine versteckten Kosten.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* PRICE STATEMENT */}
            <div className="bg-[#0B0E14] text-white rounded-2xl p-8 mb-8 text-center">
              <p className="text-lg text-gray-300 mb-6">
                Ersetze eine <span className="text-red-400 font-bold">18.000 €/Jahr Agentur</span> durch eine einmalige Investition, die weniger kostet als eine einzige Monatsrechnung.
              </p>
              
              <div className="flex flex-col items-center gap-2 mb-6">
                <div className="text-gray-500 line-through text-lg">Agentur-Kosten (12 Monate): 18.000 €</div>
                <div className="text-gray-400 line-through text-lg">Regulärer Elite-Preis: 2.999 €</div>
                <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-3 rounded-full text-2xl font-black mt-2">
                  DEIN WILLKOMMENSGESCHENK: 999 € (Einmalzahlung)
                </div>
              </div>

              <div className="bg-green-900/30 border border-green-600/50 rounded-xl p-6 max-w-2xl mx-auto">
                <h3 className="text-green-400 font-bold mb-2">💰 Der ROI-Hebel:</h3>
                <p className="text-gray-300">
                  Ein einziger Abschluss (z.B. eine private Krankenversicherung oder Altersvorsorge) bringt dir im Schnitt 
                  <span className="text-green-400 font-bold"> 1.200 € bis 2.500 € Provision</span>. 
                  Das bedeutet: Schon der erste Sale durch dieses System refinanziert dein gesamtes Marketing für den Rest deiner Karriere.
                </p>
              </div>
            </div>

            {/* CTA BUTTON */}
            <div className="text-center mb-8">
              <Button
                onClick={handlePurchaseElite}
                disabled={purchasingElite}
                className="h-16 px-12 text-xl font-black bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white rounded-full shadow-2xl transform hover:scale-105 transition-all"
                data-testid="elite-upsell-btn"
              >
                {purchasingElite ? (
                  <>
                    <Loader2 className="w-6 h-6 mr-2 animate-spin" />
                    Wird geladen...
                  </>
                ) : (
                  <>
                    JETZT AGENTUR ERSETZEN & ELITE FREISCHALTEN (999 € Einmalig)
                  </>
                )}
              </Button>
              
              <div className="flex items-center justify-center gap-4 mt-4 text-sm text-gray-600">
                <span className="flex items-center gap-1">
                  <Shield className="w-4 h-4 text-green-600" />
                  100% Sicherer Checkout
                </span>
                <span className="flex items-center gap-1">
                  <Zap className="w-4 h-4 text-green-600" />
                  Sofortige Freischaltung
                </span>
                <span className="flex items-center gap-1">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                  Einmalzahlung – Kein Abo!
                </span>
              </div>
            </div>

            {/* SKIP LINK */}
            <div className="text-center">
              <button 
                onClick={handleLogin}
                className="text-gray-500 underline hover:text-gray-700 text-sm"
              >
                Nein danke, ich möchte erst ohne ELITE starten →
              </button>
            </div>
          </>
        )}

        {/* NO UPSELL - Just show login button */}
        {!showUpsell && (
          <div className="text-center">
            <Button
              onClick={handleLogin}
              className="h-14 px-8 text-lg font-bold bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white"
            >
              Jetzt einloggen & loslegen 🚀
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default StripeSuccess;
